import * as _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { InjectedRouter } from 'react-router';

import { useDialogContext } from '../../../../contexts/DialogContext';
import { useCreateRole, useRoles, useUpdateRole } from '../../../../hooks/api';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import Loader from '../../../generic/Loader';
import Redirect from '../../../generic/Redirect';
import { InvalidPermissionError } from '../../Errors';
import type { RolePermission, RoleWithPermissions } from '../../../../../shared/model-typings';
import type { RoleScope } from '../../../../../typings/enums';

import AddEditRole from './AddEditRole';
import type { EditingRole } from './types';

const CONFIRM_UNSAVED_CHANGES_TITLE = 'Are you sure?';
const CONFIRM_UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes which will be lost by navigating away from this page. Are you sure you want to do this?';

const pickPermissionForSaving = (permission: RolePermission) =>
  _.pick(permission, ['action', 'resourceType']);

type Props = {
  routeParams?: {
    id?: string;
    roleId?: string;
  };
  router: InjectedRouter;
};

const OrganizationRolesEdit = ({ routeParams, router }: Props) => {
  const { onConfirm } = useDialogContext();
  const hasPermission = useHasPermission();
  const canManageRoles = hasPermission({
    action: Action.Manage,
    resourceType: ResourceType.Role,
  });

  const { data: roles, isLoading: isLoadingRoles } = useRoles();
  const { mutateAsync: createRole, isPending: isCreating } = useCreateRole();
  const { mutateAsync: updateRole, isPending: isUpdating } = useUpdateRole();

  const isSaving = isCreating || isUpdating;

  const isAddMode = routeParams?.id === 'add';
  const isCloneMode = !!routeParams?.roleId;

  const getEditRoleId = (): number | null => {
    if (isCloneMode) {
      return Number(routeParams?.roleId);
    }
    if (routeParams?.id && routeParams.id !== 'add') {
      return Number(routeParams.id);
    }
    return null;
  };
  const editRoleId = getEditRoleId();

  const sourceRole = useMemo<RoleWithPermissions | null>(() => {
    if (!editRoleId || !roles) {
      return null;
    }
    return roles.find((r) => r.id === editRoleId) ?? null;
  }, [editRoleId, roles]);

  const [editingRole, setEditingRole] = useState<EditingRole | null>(null);
  const [hasBeenEdited, setHasBeenEdited] = useState(false);

  useEffect(() => {
    if (isAddMode) {
      setEditingRole({
        id: null,
        name: '',
        description: '',
        scope: 'organization' as RoleScope,
        permissions: [],
        isCustom: true,
      });
      setHasBeenEdited(false);
    } else if (sourceRole) {
      setEditingRole({
        id: isCloneMode ? null : sourceRole.id,
        name: isCloneMode ? `${sourceRole.name} (copy)` : sourceRole.name,
        description: sourceRole.description,
        scope: sourceRole.scope,
        permissions: sourceRole.permissions ?? [],
        isCustom: isCloneMode ? true : sourceRole.isCustom,
      });
      setHasBeenEdited(false);
    }
  }, [isAddMode, isCloneMode, sourceRole]);

  const updateEditingRole = useCallback((updates: Partial<EditingRole>) => {
    setEditingRole((prev) => {
      if (!prev) {
        return prev;
      }
      return { ...prev, ...updates };
    });
    setHasBeenEdited(true);
  }, []);

  const navigateBackToRolesIndex = useCallback(
    () => router.push('/account/settings/organization/roles'),
    [router],
  );

  const onCancelClick = useCallback(() => {
    if (!hasBeenEdited) {
      navigateBackToRolesIndex();
      return;
    }
    onConfirm({
      title: CONFIRM_UNSAVED_CHANGES_TITLE,
      body: CONFIRM_UNSAVED_CHANGES_MESSAGE,
      onConfirm: navigateBackToRolesIndex,
    });
  }, [hasBeenEdited, navigateBackToRolesIndex, onConfirm]);

  const onSaveClick = useCallback(async () => {
    if (!editingRole) {
      return;
    }
    const roleData = {
      id: editingRole.id ?? undefined,
      name: editingRole.name,
      description: editingRole.description,
      scope: editingRole.scope,
      permissions: editingRole.permissions.map(pickPermissionForSaving),
    };

    try {
      if (isAddMode || isCloneMode) {
        await createRole(roleData);
      } else if (editingRole.id) {
        await updateRole({ ...roleData, id: editingRole.id });
      }
      navigateBackToRolesIndex();
    } catch {
      // Error toast is already shown by useToastedMutation
    }
  }, [editingRole, isAddMode, isCloneMode, createRole, updateRole, navigateBackToRolesIndex]);

  if (!canManageRoles) {
    return <InvalidPermissionError />;
  }

  if (isLoadingRoles) {
    return <Loader />;
  }

  if (isCloneMode && !sourceRole) {
    return <Redirect to="/account/settings/organization/roles" />;
  }

  if (!editingRole) {
    return <Loader />;
  }

  const isReadOnly = !isAddMode && !isCloneMode && !editingRole.isCustom;

  return (
    <AddEditRole
      name={editingRole.name}
      description={editingRole.description}
      scope={editingRole.scope}
      permissions={editingRole.permissions}
      updateEditingRole={updateEditingRole}
      isAddMode={isAddMode || isCloneMode}
      isReadOnly={isReadOnly}
      isSaving={isSaving}
      onCancelClick={onCancelClick}
      onSaveClick={onSaveClick}
    />
  );
};

export default OrganizationRolesEdit;
